import { z } from "zod"

import {
  branchIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
  membershipIdSchema,
  tenantIdSchema,
} from "./identifiers.js"

const localDateSchema = z.iso.date()
const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/)

export const fieldVisitProjectionV1Schema = z
  .object({
    visitId: entityIdSchema,
    serviceOrderId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
    timezone: z.string().min(1).max(64),
    state: z.literal("scheduled"),
    visitVersion: z.number().int().positive(),
    assignedResourceIds: z.array(entityIdSchema).max(25),
  })
  .strict()
  .refine((visit) => Date.parse(visit.startsAt) < Date.parse(visit.endsAt), {
    message: "Field Visit must end after it starts.",
    path: ["endsAt"],
  })

export const fieldWorkRelationshipV1Schema = z
  .object({
    relationshipId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    kind: z.enum(["Employment", "Contractor Engagement"]),
    branchIds: z.array(branchIdSchema).min(1).max(50),
  })
  .strict()

export const fieldWorkShiftProjectionV1Schema = z
  .object({
    workShiftId: entityIdSchema,
    relationshipId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    startingBranchId: branchIdSchema,
    state: z.enum(["Active", "On Break"]),
    version: z.number().int().positive(),
    lastEventId: entityIdSchema,
  })
  .strict()

export const fieldVisitTimeProjectionV1Schema = z
  .object({
    visitTimeId: entityIdSchema,
    visitId: entityIdSchema,
    workShiftId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    state: z.enum(["Running", "Paused"]),
    version: z.number().int().positive(),
    lastEventId: entityIdSchema,
  })
  .strict()

export const fieldLocationPolicyProjectionV1Schema = z
  .object({
    policyId: entityIdSchema,
    mode: z.enum(["Operational Events", "Continuous On Shift"]),
    intervalMinutes: z.number().int().min(5).max(120),
  })
  .strict()

export const fieldProjectionV1Schema = z
  .object({
    responseVersion: z.literal(1),
    tenantId: tenantIdSchema,
    membershipId: membershipIdSchema,
    projectionVersion: sha256Schema,
    generatedAt: z.iso.datetime({ offset: true }),
    expiresAt: z.iso.datetime({ offset: true }),
    timezone: z.string().min(1).max(64),
    horizonStartDate: localDateSchema,
    horizonEndDate: localDateSchema,
    complete: z.boolean(),
    workerId: entityIdSchema,
    relationships: z.array(fieldWorkRelationshipV1Schema).max(20),
    locationPolicy: fieldLocationPolicyProjectionV1Schema.nullable(),
    workShift: fieldWorkShiftProjectionV1Schema.nullable(),
    visitTime: fieldVisitTimeProjectionV1Schema.nullable(),
    visits: z.array(fieldVisitProjectionV1Schema).max(100),
  })
  .strict()
  .superRefine((projection, context) => {
    const generated = Date.parse(projection.generatedAt)
    const expires = Date.parse(projection.expiresAt)
    if (expires <= generated || expires - generated > 15 * 60 * 1_000) {
      context.addIssue({
        code: "custom",
        message: "Field projection expiry must be within 15 minutes.",
        path: ["expiresAt"],
      })
    }

    const horizonStart = Date.parse(`${projection.horizonStartDate}T00:00:00Z`)
    const horizonEnd = Date.parse(`${projection.horizonEndDate}T00:00:00Z`)
    if (horizonEnd - horizonStart !== 2 * 24 * 60 * 60 * 1_000) {
      context.addIssue({
        code: "custom",
        message: "Field projection horizon must be today and the next day.",
        path: ["horizonEndDate"],
      })
    }

    if (new Set(projection.visits.map((visit) => visit.visitId)).size !== projection.visits.length) {
      context.addIssue({
        code: "custom",
        message: "Field projection Visits must be unique.",
        path: ["visits"],
      })
    }
    if (
      projection.workShift &&
      !projection.relationships.some(
        (relationship) =>
          relationship.relationshipId === projection.workShift?.relationshipId &&
          relationship.legalEntityId === projection.workShift.legalEntityId,
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "Active Work Shift must use a current relationship.",
        path: ["workShift"],
      })
    }
    if (
      projection.visitTime &&
      (!projection.workShift ||
        projection.visitTime.workShiftId !== projection.workShift.workShiftId)
    ) {
      context.addIssue({
        code: "custom",
        message: "Visit Time must be nested in the active Work Shift.",
        path: ["visitTime"],
      })
    }
  })

export type FieldVisitProjectionV1 = z.infer<
  typeof fieldVisitProjectionV1Schema
>
export type FieldProjectionV1 = z.infer<typeof fieldProjectionV1Schema>
export type FieldWorkRelationshipV1 = z.infer<
  typeof fieldWorkRelationshipV1Schema
>
export type FieldWorkShiftProjectionV1 = z.infer<
  typeof fieldWorkShiftProjectionV1Schema
>
export type FieldVisitTimeProjectionV1 = z.infer<
  typeof fieldVisitTimeProjectionV1Schema
>
